// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/GameCards.tsx
// 🔧 PURPOSE: Le card dei giochi nella fila di "Intrattenimento"
//             (Entertainment), ognuna con i colori del suo gioco:
//             - Memory: il viola della sua pagina, le carte a ventaglio;
//             - Dal tarallo al trullo: il verde oliva, le tessere che cadono,
//               il record se c'è; senza accesso il lucchetto;
//             - Spara Sapori: il mare al tramonto, le bolle appese e il
//               gozzo che mira; record e lucchetto come sopra;
//             - Polpo Goloso: il rosa del polpo, il corridoio con i taralli,
//               il polpo che scappa e i gabbiani dietro; come sopra.
//             La card è tutta del colore del gioco (la "copertina"), con il
//             disegno in alto, il titolo bianco e il pulsante bianco.
// ============================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Trophy, Users } from 'lucide-react';
import { cn } from '../../lib/utils';
import { CARD_PATHS } from '../../pages/services/game/GameArt';
import type { Flavor } from '../../pages/services/sapori/engine';
import { GullSvg, PolpoSvg, SCARVES, TaralloSvg } from '../../pages/services/polpo/PolpoArt';
import { FlavorBubble, GozzoArt } from '../../pages/services/sapori/FlavorArt';
import TileArt, { tileStyle } from '../../pages/services/tarallo/TileArt';
import { FOCUS_RING } from './ui';

const CARD_WIDTH = 'w-[min(70vw,272px)] flex-shrink-0 snap-start';

const GAMES = {
  memory: {
    bg: 'linear-gradient(155deg,#A78BFA 0%,#7C3AED 45%,#4C1D95 100%)',
  },
  tarallo: {
    bg: 'linear-gradient(155deg,#9DB84A 0%,#5F7F2E 45%,#2F4216 100%)',
  },
  sapori: {
    bg: 'linear-gradient(160deg,#5CC8F0 0%,#1C8FB8 38%,#0E6482 68%,#123F55 100%)',
  },
  polpo: {
    bg: 'linear-gradient(155deg,#F7859F 0%,#D6336C 42%,#9D174D 75%,#5E0B30 100%)',
  },
} as const;

/** Le tre carte a ventaglio del Memory (come la testata della sua pagina). */
const MemoryArt: React.FC = () => (
  <svg viewBox="0 0 196 120" className="h-full w-full" aria-hidden="true" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 24l2.5-6 2.5 6-2.5 6zM176 96l2-5 2 5-2 5zM182 20h.01M10 90h.01" stroke="#FFFFFF" strokeWidth="2" strokeOpacity=".8" />
    <g transform="translate(52 66) rotate(-15)">
      <rect x="-29" y="-39" width="58" height="78" rx="9" fill="rgba(255,255,255,.18)" stroke="#FFFFFF" strokeWidth="2" strokeOpacity=".85" />
      <rect x="-23" y="-33" width="46" height="66" rx="6" stroke="#FFFFFF" strokeWidth="1.4" strokeOpacity=".45" />
      <text x="0" y="11" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontSize="30" fontWeight="700" fill="#FFFFFF">?</text>
    </g>
    <g transform="translate(100 56) rotate(2)">
      <rect x="-29" y="-39" width="58" height="78" rx="9" fill="#FFFDF9" stroke="#FFFFFF" strokeWidth="2" />
      <g transform="translate(-15.6 -17) scale(1.3)" stroke="#6D28D9" strokeWidth="1.8"><path d={CARD_PATHS.wine} /></g>
    </g>
    <g transform="translate(148 68) rotate(16)">
      <rect x="-29" y="-39" width="58" height="78" rx="9" fill="#FFFDF9" stroke="#FFFFFF" strokeWidth="2" />
      <g transform="translate(-15.6 -17) scale(1.3)" stroke="#6D28D9" strokeWidth="1.8"><path d={CARD_PATHS.pizza} /></g>
    </g>
  </svg>
);

/** Le tessere del 2048 che "cadono": tarallo, orecchietta, burrata e il trullo davanti. */
const TaralloArt: React.FC = () => (
  <div className="relative h-full w-full" aria-hidden="true">
    {[
      { v: 2, cls: 'left-[6%] top-[22%] -rotate-12 h-[50%]' },
      { v: 8, cls: 'left-[27%] top-[2%] rotate-6 h-[46%]' },
      { v: 64, cls: 'right-[6%] top-[14%] rotate-12 h-[50%]' },
      { v: 2048, cls: 'left-[36%] top-[34%] -rotate-3 h-[62%] z-10' },
    ].map((t) => (
      <span
        key={t.v}
        className={cn('absolute flex aspect-square items-center justify-center rounded-[14px] shadow-[0_10px_22px_rgba(0,0,0,.28)]', t.cls)}
        style={{ background: tileStyle(t.v).bg }}
      >
        <span className="block h-[74%] w-[74%]">
          <TileArt value={t.v} />
        </span>
      </span>
    ))}
  </div>
);

/**
 * "Spara Sapori": il grappolo di bolle appeso in alto a destra, la mira a
 * puntini e il gozzo sul mare, con il sole del tramonto.
 */
const SaporiArt: React.FC = () => (
  <span className="absolute inset-0 block" aria-hidden="true">
    <span className="absolute bottom-[6px] left-[14px] h-[46px] w-[46px] rounded-full bg-[#FFD884]/70 blur-[10px]" />
    <svg viewBox="0 0 272 132" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 h-full w-full" fill="none">
      <path d="M0 112c22-5 44-5 66 0s44 5 66 0 44-5 66 0 44 5 74 0V132H0z" fill="#0B3F57" fillOpacity=".55" />
      <path d="M72 94 L112 80" stroke="#FFFFFF" strokeWidth="3.2" strokeLinecap="round" strokeDasharray="0.1 9" />
    </svg>
    {/* Il grappolo sotto le pillole, a destra; il limone in volo verso i limoni. */}
    {([
      { f: 'tomato', cls: 'right-[84px] top-[38px]' },
      { f: 'sea', cls: 'right-[48px] top-[38px]' },
      { f: 'olive', cls: 'right-[12px] top-[38px]' },
      { f: 'lemon', cls: 'right-[102px] top-[69px]' },
      { f: 'fig', cls: 'right-[66px] top-[69px]' },
      { f: 'burrata', cls: 'right-[30px] top-[69px]' },
    ] as Array<{ f: Flavor; cls: string }>).map((b) => (
      <FlavorBubble key={b.f} flavor={b.f} className={cn('absolute h-[36px] w-[36px] drop-shadow-[0_4px_8px_rgba(0,0,0,.25)]', b.cls)} />
    ))}
    <FlavorBubble flavor="lemon" className="absolute left-[104px] top-[62px] z-10 h-[30px] w-[30px] drop-shadow-[0_4px_8px_rgba(0,0,0,.3)]" />
    <GozzoArt className="absolute bottom-[4px] left-[18px] z-20 h-[44px] w-[100px]" />
  </span>
);

/**
 * "Polpo Goloso": un corridoio del labirinto, i taralli davanti al polpo
 * che scappa e due gabbiani dietro.
 */
const PolpoArt: React.FC = () => (
  <span className="absolute inset-0 block" aria-hidden="true">
    <span className="absolute inset-x-3 top-[58px] h-[56px] rounded-[16px] border-2 border-white/35 bg-black/10" />
    <span className="absolute left-[22px] top-[76px] flex gap-[11px]">
      {[0, 1, 2, 3].map((i) => <TaralloSvg key={i} className="h-[18px] w-[18px]" />)}
    </span>
    <PolpoSvg className="absolute left-[118px] top-[56px] z-10 h-[56px] w-[56px] drop-shadow-[0_6px_10px_rgba(0,0,0,.3)]" />
    <GullSvg scarf={SCARVES[0]} className="absolute right-[56px] top-[60px] h-[48px] w-[48px] -scale-x-100 drop-shadow-[0_6px_10px_rgba(0,0,0,.25)]" />
    <GullSvg scarf={SCARVES[2]} className="absolute right-[10px] top-[64px] h-[44px] w-[44px] -scale-x-100 drop-shadow-[0_6px_10px_rgba(0,0,0,.25)]" />
  </span>
);

interface CardProps {
  onClick: () => void;
}

const Pill: React.FC<{ children: React.ReactNode; className?: string; style?: React.CSSProperties }> = ({ children, className, style }) => (
  <span
    className={cn('inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[10.5px] font-extrabold uppercase leading-none tracking-[0.1em]', className)}
    style={style}
  >
    {children}
  </span>
);

export const MemoryGameCard: React.FC<CardProps> = ({ onClick }) => {
  const { t } = useTranslation();
  const g = GAMES.memory;
  return (
    <li className={CARD_WIDTH}>
      <button
        type="button"
        onClick={onClick}
        className={cn('relative flex h-full w-full flex-col overflow-hidden rounded-[22px] text-left shadow-[0_10px_28px_rgba(76,29,149,.18)]', FOCUS_RING)}
        style={{ background: g.bg }}
      >
        <span className="relative block h-[132px] w-full flex-shrink-0">
          <span className="absolute left-3 top-3 z-10">
            <Pill className="bg-white/90 text-[#5B21B6]">
              <Users className="h-3 w-3" aria-hidden="true" />
              {t('home.fun.forEveryone')}
            </Pill>
          </span>
          <span className="absolute inset-x-2 bottom-0 top-5 block">
            <MemoryArt />
          </span>
        </span>
        <span className="flex flex-1 flex-col gap-1 px-3.5 pb-3.5 pt-3">
          <span className="font-display text-[22px] font-bold leading-tight text-white">{t('home.fun.memoryTitle')}</span>
          <span className="text-[13px] leading-snug text-violet-100">{t('home.fun.memoryDesc')}</span>
          {/* Il bottone è disegnato, non un secondo bersaglio: tutta la card apre il gioco. */}
          <span
            aria-hidden="true"
            className="mt-auto inline-flex min-h-[42px] w-full items-center justify-center rounded-xl bg-white px-4 text-[12.5px] font-extrabold uppercase tracking-button text-[#5B21B6]"
          >
            {t('home.fun.play')}
          </span>
        </span>
      </button>
    </li>
  );
};

interface RankedCardProps extends CardProps {
  locked: boolean;
  best: number;
}

/**
 * La card di un gioco con la classifica (solo dopo l'accesso): "Nuovo" e
 * "Classifica" in alto, il record se c'è, senza accesso il lucchetto.
 */
const RankedGameCard: React.FC<RankedCardProps & {
  bg: string;
  shadow: string;
  /** Il testo del pulsante e della pillola "Classifica" (il colore scuro del gioco). */
  ink: string;
  /** Il colore della descrizione, chiaro sul fondo. */
  soft: string;
  title: string;
  desc: string;
  art: React.ReactNode;
}> = ({ onClick, locked, best, bg, shadow, ink, soft, title, desc, art }) => {
  const { t, i18n } = useTranslation();
  return (
    <li className={CARD_WIDTH}>
      <button
        type="button"
        onClick={onClick}
        className={cn('relative flex h-full w-full flex-col overflow-hidden rounded-[22px] text-left', shadow, FOCUS_RING)}
        style={{ background: bg }}
      >
        <span className="relative block h-[132px] w-full flex-shrink-0">
          <span className="absolute left-3 top-3 z-20 flex gap-1.5">
            <Pill className="bg-nva-gold text-nva-espresso">{t('home.fun.new')}</Pill>
            <Pill className="bg-white/90" style={{ color: ink }}>
              <Trophy className="h-3 w-3" aria-hidden="true" />
              {t('home.fun.leaderboard')}
            </Pill>
          </span>
          {art}
        </span>
        <span className="flex flex-1 flex-col gap-1 px-3.5 pb-3.5 pt-3">
          <span className="font-display text-[22px] font-bold leading-tight text-white">{title}</span>
          <span className="text-[13px] leading-snug" style={{ color: soft }}>
            {!locked && best > 0
              ? t('home.fun.yourBest', { score: new Intl.NumberFormat(i18n.language || 'it').format(best) })
              : desc}
          </span>
          <span
            aria-hidden="true"
            className={cn(
              'mt-auto inline-flex min-h-[42px] w-full items-center justify-center gap-1.5 rounded-xl px-4 text-[12.5px] font-extrabold uppercase tracking-button',
              locked ? 'border border-white/50 bg-white/15 text-white' : 'bg-white',
            )}
            style={locked ? undefined : { color: ink }}
          >
            {locked ? <Lock className="h-3.5 w-3.5" aria-hidden="true" /> : null}
            {locked ? t('home.fun.loginToPlay') : t('home.fun.play')}
          </span>
        </span>
      </button>
    </li>
  );
};

export const TaralloGameCard: React.FC<RankedCardProps> = (props) => {
  const { t } = useTranslation();
  return (
    <RankedGameCard
      {...props}
      bg={GAMES.tarallo.bg}
      shadow="shadow-[0_10px_28px_rgba(47,66,22,.22)]"
      ink="#3E5A1E"
      soft="#E6EFC8"
      title={t('home.fun.taralloTitle')}
      desc={t('home.fun.taralloDesc')}
      art={(
        <span className="absolute inset-x-3 bottom-1 top-8 block">
          <TaralloArt />
        </span>
      )}
    />
  );
};

export const SaporiGameCard: React.FC<RankedCardProps> = (props) => {
  const { t } = useTranslation();
  return (
    <RankedGameCard
      {...props}
      bg={GAMES.sapori.bg}
      shadow="shadow-[0_10px_28px_rgba(22,78,99,.24)]"
      ink="#0E5A73"
      soft="#D7F0FA"
      title={t('home.fun.saporiTitle')}
      desc={t('home.fun.saporiDesc')}
      art={<SaporiArt />}
    />
  );
};

export const PolpoGameCard: React.FC<RankedCardProps> = (props) => {
  const { t } = useTranslation();
  return (
    <RankedGameCard
      {...props}
      bg={GAMES.polpo.bg}
      shadow="shadow-[0_10px_28px_rgba(122,18,64,.24)]"
      ink="#8E1747"
      soft="#FFE1EA"
      title={t('home.fun.polpoTitle')}
      desc={t('home.fun.polpoDesc')}
      art={<PolpoArt />}
    />
  );
};
